import { useEffect, useRef, useState } from 'react';
import Spinner from 'react-spinkit';
import {
  getCurrentYearTeachersData,
  getTotalRow,
  getLegacyStudentLogins,
  getNewStudentLogins,
  getLegacyStoryOpens,
  getNewStoryOpens,
} from '../reportUtilities';
import SortableTable from '../Tables/SortableTable';
import { cloneDeep } from 'lodash';
import styles from './TableStyles.module.scss';
import {
  ReportStudent,
  ReportingMixpanelEvent,
  TeacherClassroomMaps,
} from '../../../peekapak-types/ReportDataExchangeTypes';
import { UserProfileType } from '../../../peekapak-types/DataProtocolTypes';
import { Assignment } from '../../../peekapak-types/LessonPlanTypes';

interface OverallUsageProps {
  errorMessage: string;
  assignmentsMap: Record<string, Assignment[]>;
  assignmentsMapReady: boolean;
  journalsMap: Record<
    string,
    {
      classroomId: string;
      createdAt: number;
      feeling: number;
      type: string;
      userId: string;
    }[]
  >;
  journalsMapReady: boolean;
  journalsMapLoadProgress?: string;
  assignmentsMapLoadProgress?: string;
  legacyPeekavilleEvents: ReportingMixpanelEvent[];
  legacyPeekavilleEventsReady: boolean;
  teacherStudentMap: Record<string, ReportStudent[]>;
  teacherStudentMapReady: boolean;
  teacherClassroomMaps: TeacherClassroomMaps;
  teacherClassroomMapsReady: boolean;
  userList: UserProfileType[];
  legacyPlatformEvents: ReportingMixpanelEvent[];
  legacyPlatformEventsReady: boolean;
  newMixpanelEvents: ReportingMixpanelEvent[];
  newMixpanelEventsReady: boolean;
}

const StudentsSecondary = ({
  teacherStudentMapReady,
  teacherStudentMap,
  teacherClassroomMaps,
  teacherClassroomMapsReady,
  userList,
  legacyPeekavilleEvents,
  legacyPeekavilleEventsReady,
  journalsMap,
  journalsMapReady,
  journalsMapLoadProgress,
  assignmentsMapLoadProgress,
  assignmentsMap,
  assignmentsMapReady,
  errorMessage,
  legacyPlatformEvents,
  legacyPlatformEventsReady,
  newMixpanelEvents,
  newMixpanelEventsReady,
}: OverallUsageProps) => {
  const [myState, setMyState] = useState({
    dataMap: {} as Record<
      string,
      {
        events: ReportingMixpanelEvent[];
        journals: {
          classroomId: string;
          createdAt: number;
          feeling: number;
          type: string;
          userId: string;
        }[];
        assignments: Assignment[];
        firstName: string;
        middleName: string;
        lastName: string;
        userId: string;
        username: string;
        grade: number;
        language: string;
      }[]
    >,
    dataMapReady: false,
  });

  // const prevMixpanelEventsReady = useRef(legacyPeekavilleEventsReady);

  // useEffect(() => {
  //   if (prevMixpanelEventsReady.current && !legacyPeekavilleEventsReady) {
  //     setMyState((prevState) => ({
  //       ...prevState,
  //       dataMap: {},
  //       dataMapReady: false,
  //     }));
  //   }
  //   prevMixpanelEventsReady.current = legacyPeekavilleEventsReady;
  // }, [legacyPeekavilleEventsReady]);

  useEffect(() => {
    setMyState((prevState) => ({
      ...prevState,
      dataMap: {},
      dataMapReady: false,
    }));
  }, [legacyPeekavilleEvents, legacyPlatformEvents, newMixpanelEvents, journalsMap, assignmentsMap]);

  useEffect(() => {
    if (
      journalsMapReady &&
      assignmentsMapReady &&
      legacyPeekavilleEventsReady &&
      teacherStudentMapReady &&
      teacherClassroomMapsReady &&
      legacyPlatformEventsReady &&
      newMixpanelEventsReady &&
      !myState.dataMapReady
    ) {
      buildData();
    }
    function buildData() {
      const newMap = buildMapWithAssignments();
      setMyState((prevState) => ({
        ...prevState,
        dataMap: newMap,
        dataMapReady: true,
      }));
    }
  }, [
    teacherClassroomMapsReady,
    legacyPeekavilleEventsReady,
    legacyPeekavilleEvents,
    teacherStudentMap,
    teacherStudentMapReady,
    journalsMap,
    journalsMapReady,
    myState.dataMapReady,
    legacyPlatformEventsReady,
    newMixpanelEventsReady,
    userList,
  ]);

  function buildMapWithAssignments() {
    const newMap = {} as Record<
      string,
      {
        events: ReportingMixpanelEvent[];
        journals: {
          classroomId: string;
          createdAt: number;
          feeling: number;
          type: string;
          userId: string;
        }[];
        assignments: Assignment[];
        firstName: string;
        middleName: string;
        lastName: string;
        userId: string;
        username: string;
        grade: number;
        language: string;
      }[]
    >;
    Object.keys(teacherStudentMap).forEach((teacherKey) => {
      const newStudentsArray = teacherStudentMap[teacherKey]
        .filter((s) => s.grade > 5)
        .map((s) => {
          const events = [...legacyPlatformEvents, ...newMixpanelEvents].filter((event) => event.userId === s.userId);

          return {
            ...s,
            events,
            journals: journalsMap[s.userId],
            assignments: assignmentsMap[s.userId] ? assignmentsMap[s.userId] : [],
          };
        });

      newMap[teacherKey] = newStudentsArray;
    });
    return newMap;
  }

  const message = getDataNotReadyMessage();
  if (message) {
    return message;
  }

  const staticMessage = getStaticMessage();
  if (staticMessage) {
    return message;
  }

  const rosteredTeachers = getCurrentYearTeachersData(
    userList,
    teacherClassroomMaps,
    legacyPlatformEvents,
    newMixpanelEvents,
  );

  const sortedList = cloneDeep(rosteredTeachers).sort((a, b) => (a.lastName || '').localeCompare(b.lastName || ''));

  const tableData = sortedList.map((e) => {
    return {
      teacher_name: `${e.lastName || ''}, ${e.firstName || ''}`,
      rostered_student: getNumberOfRosteredStudents(e.email),
      student_logins: getNumberOfStudentLogins(e.email),
      todos_assigned: getNumberAssigned(e.email),
      todos_completed: getNumberCompleted(e.email),
      story_opens: getNumberOfStoryOpens(e.email),
      feelings_checkins: getNumberOfJournals(e.email),
      email: e.email,
      userId: e.userId,
    };
  });

  const columns = [
    {
      label: "Teacher's Name",
      accessor: 'teacher_name',
      sortable: false,
      renderTooltip: (data: { email?: string; userId?: string }) => data.email || data.userId || null,
    },
    {
      label: 'Number of Students',
      accessor: 'rostered_student',
      sortable: true,
    },
    {
      label: 'Student Logins',
      accessor: 'student_logins',
      sortable: true,
    },
    {
      label: 'To Dos Assigned',
      accessor: 'todos_assigned',
      sortable: true,
    },
    {
      label: 'To Dos Completed',
      accessor: 'todos_completed',
      sortable: true,
    },
    {
      label: 'Story Opens',
      accessor: 'story_opens',
      sortable: true,
    },
    {
      label: 'Feelings Check-Ins',
      accessor: 'feelings_checkins',
      sortable: true,
    },
  ];

  const totalRow = getTotalRow(tableData);

  // console.debug(
  //   `%cStudent Analytics active students`,
  //   'background: blue; color: yellow',
  //   totalActiveStudents
  // );

  return (
    <div>
      <SortableTable data={tableData} columns={columns} totals={totalRow} />
    </div>
  );

  function getStaticMessage() {
    if (userList.length === 0) {
      return (
        <div className={styles.messageContainer}>
          <div className={styles.progressMessage}>There are no users in this school</div>
        </div>
      );
    }

    if (errorMessage) {
      return (
        <div className={styles.messageContainer}>
          <div className={styles.progressMessage}>
            {`We're sorry, there's a problem retrieving data. Please contact
            Peekapak support and provide them with this error message:`}
            {errorMessage}
          </div>
        </div>
      );
    }
  }

  function getDataNotReadyMessage() {
    let message = null;

    if (!teacherStudentMapReady) {
      message = 'Matching students to teachers...';
    } else if (!legacyPeekavilleEventsReady) {
      message = 'Loading game events data...';
    } else if (!journalsMapReady) {
      message = `Loading student check-in data ${journalsMapLoadProgress}...`;
    } else if (!assignmentsMapReady) {
      message = `Loading assignments data ${assignmentsMapLoadProgress}...`;
    } else if (!myState.dataMapReady) {
      message = 'Calculating stats...';
    }

    if (message) {
      return (
        <div className={styles.messageContainer}>
          <div className={styles.progressMessage}>
            {message}
            <Spinner name='line-scale' />
            This may take up to 10 minutes or more
          </div>
        </div>
      );
    }

    return null;
  }

  function getNumberOfJournals(email: string) {
    const studentArray = myState.dataMap[email];
    if (studentArray && studentArray.length > 0) {
      const numberOfJournalEntries = studentArray.map((s) => {
        if (s.journals) {
          return s.journals.reduce((acc, _j) => acc + 1, 0);
        }

        return 0;
      });

      return numberOfJournalEntries.reduce((acc, e) => acc + e, 0);
    }
    return 0;
  }

  function getNumberOfStoryOpens(email: string) {
    const studentArray = myState.dataMap[email];
    if (studentArray && studentArray.length > 0) {
      const eventsArray = studentArray.reduce(
        (acc, s) => [...acc, ...s.events],
        [] as unknown as ReportingMixpanelEvent[],
      );

      return getLegacyStoryOpens(eventsArray) + getNewStoryOpens(eventsArray);
    }
    return 0;
  }

  function getNumberAssigned(email: string) {
    const studentArray = myState.dataMap[email];
    if (studentArray && studentArray.length > 0) {
      return studentArray.reduce((acc, s) => {
        return s.assignments && s.assignments.length > 0 ? acc + s.assignments.length : acc;
      }, 0);
    } else return 0;
  }

  function getNumberCompleted(email: string) {
    const studentArray = myState.dataMap[email];
    if (studentArray && studentArray.length > 0) {
      return studentArray.reduce((acc, s) => {
        return s.assignments && s.assignments.length > 0
          ? acc + s.assignments.filter((a) => a.status === 'submitted').length
          : acc;
      }, 0);
    } else return 0;
  }

  function getNumberOfStudentLogins(email: string) {
    const studentArray = myState.dataMap[email];
    const eventsArray = studentArray.reduce(
      (acc, s) => [...acc, ...s.events],
      [] as unknown as ReportingMixpanelEvent[],
    );

    return getLegacyStudentLogins(eventsArray) + getNewStudentLogins(eventsArray);

    return 0;
  }

  function getNumberOfRosteredStudents(email: string) {
    const studentArray = myState.dataMap[email];
    if (studentArray && studentArray.length > 0) {
      return studentArray.length;
    }
    return 0;
  }
};

export default StudentsSecondary;
